import React from "react";

import { Button } from "@common/components/Button";
import { Ellipsis } from "@common/components/Ellipsis";
import { Flex } from "@common/components/Flex";
import { FormControl } from "@common/components/form-control";
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from "@common/components/modal";
import Dropdown from "@common/containers/fields/Dropdown";
import { trDts } from "@modules/datasheets/utils/i18n";
import FormatManagement from "@common/components/FormatManagement";
import { MessageBox } from "@common/components/MessageBox";

import { DatasheetFormatManagementProps } from "./datasheet-format-management.types";
import { useController } from "./datasheet-format-management.controller";

import "./datasheet-format-management.styles.scss";

export const DatasheetFormatManagement: React.FC<DatasheetFormatManagementProps> = (props) => {
  const { onClose, getId, isAllowedToSubmit, isAllowedToSubmitFormatManagementSubmit } = props;

  const {
    defaultFormatTypes,
    selectedFormatType,
    formatName,
    folders,
    formats,
    selectedFolder,
    selectedFormat,
    folderPickerOpened,
    getAndSetFolders,
    handleFolderSelect,
    getAndSetFormats,
    handleSubmit,
    openFolderPicker,
    closeFolderPicker,
    handleFormatNameChange,
    setSelectedFormatType,
    resetFormats,
    modeConfig,
    closeMessage,
    messageProps,
  } = useController(props);

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="DatasheetFormatManagement"
      dialogClassName="DatasheetFormatManagement__dialog"
      animation={false}
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle id={getId.title}>{modeConfig.title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <MessageBox hideMessageBox={closeMessage} {...messageProps} />
        <Flex justify="center">
          <div className="DatasheetFormatManagement__content">
            <Flex className="DatasheetFormatManagement__inner" alignItems="center">
              <span className="DatasheetFormatManagement__element-label">{trDts("Type")}</span>
              <div style={{ width: "100%" }}>
                <Dropdown
                  id={getId.formatTypes}
                  placeholder={trDts("Select")}
                  selectedValue={selectedFormatType}
                  searchable={false}
                  onValueChanged={setSelectedFormatType}
                  options={defaultFormatTypes}
                />
              </div>
            </Flex>
            <Flex alignItems="center" width="100%" className="DatasheetFormatManagement__inner">
              <span className="DatasheetFormatManagement__element-label">{trDts("Folder")}</span>
              <Flex width="100%">
                <Ellipsis
                  className="DatasheetFormatManagement__folder-chooser"
                  text={selectedFolder?.name}
                  id={getId.selectedFolderNameHolder}
                />
                <Button
                  className="DatasheetFormatManagement__folder-chooser__open-btn"
                  variant="text"
                  id={getId.folderChooserBtn}
                  onClick={openFolderPicker}
                >
                  <img src="gif/folder_open.gif" />
                </Button>
              </Flex>
            </Flex>
            {folderPickerOpened && (
              <FormatManagement
                selectedFolder={selectedFolder}
                selectedFormat={selectedFormat}
                title={modeConfig.formatManagement.title}
                submitBtnProps={modeConfig.formatManagement.submitBtnProps}
                close={closeFolderPicker}
                folders={folders}
                formats={formats}
                isAllowedToSubmit={
                  isAllowedToSubmitFormatManagementSubmit
                    ? isAllowedToSubmitFormatManagementSubmit
                    : modeConfig.formatManagement.isAllowedSubmit
                }
                getFormats={getAndSetFormats}
                resetFormats={resetFormats}
                getFolders={getAndSetFolders}
                onSubmit={handleFolderSelect}
                deleteFormatBtnProps={modeConfig.formatManagement.deleteFormatBtnProps}
                onFormatDelete={modeConfig.formatManagement.handleFormatDelete}
                onFolderDelete={modeConfig.formatManagement.handleFolderDelete}
                onFolderCreate={modeConfig.formatManagement.handleFolderCreate}
              />
            )}
            {modeConfig.showFormatName && (
              <Flex alignItems="center" className="DatasheetFormatManagement__inner">
                <span className="DatasheetFormatManagement__element-label">{trDts("Name")}</span>
                <FormControl id={getId.formatName} value={formatName} onChange={handleFormatNameChange} />
              </Flex>
            )}
          </div>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button id="btn-fm-cancel" onClick={onClose}>
            {trDts("Cancel")}
          </Button>
          <Button
            id="btn-fm-load"
            disabled={
              isAllowedToSubmit
                ? !isAllowedToSubmit({
                    folderId: selectedFolder?.id,
                    formatId: selectedFormat?.id,
                    newFormatName: formatName,
                  })
                : !modeConfig.isAllowedToSubmit({
                    folderId: selectedFolder?.id,
                    formatId: selectedFormat?.id,
                    newFormatName: formatName,
                  })
            }
            bsStyle="primary"
            onClick={handleSubmit}
          >
            {modeConfig.submitBtnText}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
